import { createContext, useContext, useEffect, useState } from "react"

// --- Only Light and Dark ---
type Theme = "dark" | "light"

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme // Default can still be light or dark
}

// --- Context only needs theme and setter ---
type ThemeContextType = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({
  children,
  defaultTheme = "light", // Default to light if not specified
}: ThemeProviderProps) {
  // State for the current theme (light or dark)
  const [theme, setThemeState] = useState<Theme>(
    () => (localStorage.getItem("theme") as Theme) || defaultTheme
  )

  // Effect to apply theme class on initial load and when theme changes
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme); // Add the current theme class
    localStorage.setItem("theme", theme); // Save preference
    window.dispatchEvent(new CustomEvent("theme-change")); // Dispatch event
  }, [theme]); // Rerun effect when theme changes

  // Function to update theme preference
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme); // Update the state, triggering the useEffect
  };

  return (
    <ThemeContext.Provider
      value={{
        theme, // Provide current theme
        setTheme, // Provide function to change theme
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

// Custom hook remains the same, but returns the simplified context type
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext)

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }

  return context
}